const getPresetConfig = (preset) => {
  switch (preset) {
    case 'loop':
      return { loop: true };
    case 'scroll':
      return { slidesToScroll: 1 };
    case 'contain':
      return { containScroll: 'trimSnaps' };
    case 'dragFree':
      return { dragFree: true };
    case 'snaps':
      return { skipSnaps: false };
    default:
      return {};
  }
};

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import './carousel-base.css';
import './carousel.css';
import { getButtonClasses, isColorDark } from '../../../config/colors';

// Utility to derive a friendly class from the color value & label
const colorToClass = (value, label) => {
  if (!value) return '';
  const varMatch = /^var\(--([^)]+)\)/.exec(value);
  if (varMatch) return `color-var-${varMatch[1].toLowerCase()}`;
  if (value.startsWith('#')) return `color-${value.slice(1).toLowerCase()}`;
  if (label) return `color-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return '';
};

class CarouselErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

const EmblaCarousel = ({ data, id }) => {
  const dispatch = useDispatch();
  const searchResults = useSelector((state) => state.search?.subrequests?.[id]);
  const allSubrequests = useSelector(
    (state) => state.search?.subrequests || {},
  );

  // Resolved path map: uid -> '/site/path'
  const [resolvedPaths, setResolvedPaths] = useState({});

  // When a resolveuid subrequest lands, extract the path from its first result
  useEffect(() => {
    if (!data.useListing || !data.query?.query) return;
    const uidCriteria = data.query.query.filter(
      (c) =>
        c.i === 'path' &&
        c.o === 'plone.app.querystring.operation.string.absolutePath',
    );
    uidCriteria.forEach((c) => {
      if (typeof c.v !== 'string') return;
      const uid = c.v.split('::')[0];
      if (resolvedPaths[uid]) return;
      const resolveId = `${id}-resolveuid-${uid}`;
      const result = allSubrequests[resolveId];
      if (result?.items?.length > 0) {
        const path = flattenToAppURL(result.items[0]['@id']);
        setResolvedPaths((prev) => ({ ...prev, [uid]: path }));
      }
    });
  }, [allSubrequests, data.useListing, data.query, id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!data.useListing || !data.query?.query) return;
    const uidCriteria = data.query.query.filter(
      (c) =>
        c.i === 'path' &&
        c.o === 'plone.app.querystring.operation.string.absolutePath',
    );
    if (!uidCriteria.length) return;

    // Parse "uid::depth" — resolve each unique UID we haven't seen yet
    const toResolve = uidCriteria
      .map((c) => (typeof c.v === 'string' ? c.v.split('::')[0] : null))
      .filter(Boolean)
      .filter((uid) => !resolvedPaths[uid]);

    if (!toResolve.length) return;

    toResolve.forEach((uid) => {
      const resolveId = `${id}-resolveuid-${uid}`;
      dispatch(
        searchContent(
          '',
          {
            UID: uid,
            metadata_fields: [],
            b_size: 1,
          },
          resolveId,
        ),
      );
    });
  }, [data.useListing, data.query]); // eslint-disable-line react-hooks/exhaustive-deps

  // Returns true when a URL's final path segment looks like a raw image file.
  // Plone objects with these extensions can't be reached via @@images traversal.
  const hasImageExtension = (url) => {
    if (!url) return false;
    return /\.(svg|jpg|jpeg|png|gif|webp|avif)(\?.*)?$/i.test(url);
  };

  const resolveScales = (scales, basePath) => {
    try {
      if (!scales) return null;
      const scale =
        scales.preview ||
        scales.large ||
        scales.teaser ||
        scales.larger ||
        Object.values(scales)[0];
      if (!scale?.download) return null;
      const download = scale.download;
      if (download.startsWith('http') || download.startsWith('/'))
        return flattenToAppURL(download);
      // base_path may be a resolveuid URL — always flatten it before joining
      const flatBase = basePath ? flattenToAppURL(basePath) : null;
      if (!flatBase) return null;
      // If the base path itself looks like a raw image file, @@images traversal will fail —
      // bail out so the caller can fall through to a direct URL strategy instead.
      if (hasImageExtension(flatBase)) return null;
      return `${flatBase}/${download}`;
    } catch (e) {
      return null;
    }
  };

  // Resolve the best available image URL from a catalog brain or content object.
  // Primary strategy: direct @@images traversal (same pattern as ResponsiveImage).
  // image_scales parsing is only used for linked images where the image lives on a
  // different object and we need to derive its base_path.
  const getImageUrl = (item) => {
    try {
      const baseUrl =
        item.url || (item['@id'] ? flattenToAppURL(item['@id']) : null);

      // 1. Own image field — direct @@images traversal, most reliable.
      //    Skip if image_scales[image_field] is an array, which means the image is
      //    linked from a different object (its base_path ≠ item['@id']).
      //    Also skip @@images for SVG items — Plone can't traverse @@images on paths
      //    ending in .svg (the extension confuses request routing). Serve directly instead.
      if (
        baseUrl &&
        item.image_field &&
        !Array.isArray(item.image_scales?.[item.image_field])
      ) {
        // Objects whose @id ends with an image extension can't use @@images traversal
        if (hasImageExtension(baseUrl)) return baseUrl;
        return `${baseUrl}/@@images/${item.image_field}`;
      }

      // 2. Linked preview image (preview_image_scales) — image lives on a different object.
      if (item.preview_image_scales && item.preview_image_field) {
        const fieldScales = item.preview_image_scales[item.preview_image_field];
        if (Array.isArray(fieldScales) && fieldScales.length > 0) {
          const entry = fieldScales[0];
          const url = resolveScales(entry.scales, entry.base_path);
          if (url) return url;
          if (entry.download && entry.base_path) {
            return `${flattenToAppURL(entry.base_path)}/${entry.download}`;
          }
          if (entry.base_path) {
            return `${flattenToAppURL(entry.base_path)}/@@images/${item.preview_image_field}`;
          }
        }
      }

      // 3. Linked image via image_scales (array format means image is on another object).
      if (item.image_scales && item.image_field) {
        const fieldScales = item.image_scales[item.image_field];
        if (Array.isArray(fieldScales) && fieldScales.length > 0) {
          const entry = fieldScales[0];
          const url = resolveScales(entry.scales, entry.base_path);
          if (url) return url;
          if (entry.download && entry.base_path) {
            return `${flattenToAppURL(entry.base_path)}/${entry.download}`;
          }
          if (entry.base_path) {
            return `${flattenToAppURL(entry.base_path)}/@@images/${item.image_field}`;
          }
        }
        // Non-array image_scales — own image, use direct traversal (url was null above).
        if (baseUrl) return `${baseUrl}/@@images/${item.image_field}`;
      }

      // 4. lead_image
      if (item.lead_image) {
        if (item.lead_image['@id'])
          return `${flattenToAppURL(item.lead_image['@id'])}/@@images/image`;
        const url = resolveScales(item.lead_image.scales, null);
        if (url) return url;
      }

      // 5. Direct image field object
      if (item.image) {
        const image = Array.isArray(item.image) ? item.image[0] : item.image;
        return image?.['@id']
          ? `${flattenToAppURL(image['@id'])}/@@images/image`
          : image?.download || '';
      }

      return '';
    } catch (e) {
      return '';
    }
  };

  const getListingImageUrl = (item) => {
    const url = flattenToAppURL(item['@id'] || '');
    // Objects whose @id ends with an image extension can't use @@images traversal
    if (hasImageExtension(url)) return url;
    if (item['@type'] === 'Image') return `${url}/@@images/image`;
    return getImageUrl(item);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (e) {
      return dateString;
    }
  };

  const renderDate = (slide) => {
    const mode = data.dateDisplay;
    if (!mode || mode === 'none') return null;
    if (mode === 'effective') {
      if (!slide.effectiveDate) return null;
      return <p className="slide-date">{formatDate(slide.effectiveDate)}</p>;
    }
    if (mode === 'start') {
      if (slide.startDate) {
        return (
          <p className="slide-date">Starts {formatDate(slide.startDate)}</p>
        );
      }
      if (slide.effectiveDate) {
        return <p className="slide-date">{formatDate(slide.effectiveDate)}</p>;
      }
      return null;
    }
    return null;
  };
  const getSlides = () => {
    let slides = [];
    if (
      data.useListing &&
      searchResults?.items &&
      searchResults.items.length > 0
    ) {
      let items = searchResults.items;

      // @search doesn't honour Subject.operator natively, so we enforce AND/OR client-side.
      const subjectCriterion = data.query?.query?.find(
        (c) => c.i === 'Subject',
      );
      if (
        subjectCriterion &&
        Array.isArray(subjectCriterion.v) &&
        subjectCriterion.v.length > 1
      ) {
        if (
          subjectCriterion.o === 'plone.app.querystring.operation.selection.all'
        ) {
          // AND: item must have every selected tag
          items = items.filter((item) => {
            const subjects = item.Subject || [];
            return subjectCriterion.v.every((tag) => subjects.includes(tag));
          });
        } else if (
          subjectCriterion.o === 'plone.app.querystring.operation.selection.any'
        ) {
          // OR: item must have at least one selected tag
          items = items.filter((item) => {
            const subjects = item.Subject || [];
            return subjectCriterion.v.some((tag) => subjects.includes(tag));
          });
        }
      }

      slides = items.map((item) => ({
        heading: item.title || '',
        content: item.description || '',
        image: null,
        link: item,
        buttonText: data.listingButtonText || 'Read more',
        effectiveDate: item.effective || null,
        startDate: item.start || null,
        subjects: item.Subject || [],
        '@id': item['@id'] || null,
        url: item['@id'] ? flattenToAppURL(item['@id']) : null,
        image_field: item.image_field || null,
        image_scales: item.image_scales || null,
        preview_image_scales: item.preview_image_scales || null,
        preview_image_field: item.preview_image_field || null,
        lead_image: item.lead_image || null,
        isFromListing: true,
      }));
    }
    if (!data.useListing || (data.useListing && data.appendManualSlides)) {
      const manualSlides = (data.slides || []).map((slide) => ({
        ...slide,
        isFromListing: false,
      }));
      slides = slides.concat(manualSlides);
    }
    return slides;
  };

  // ---- Tag filter state ----
  const parsedFilterTags = React.useMemo(() => {
    if (!data.filterTags) return [];
    return data.filterTags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
  }, [data.filterTags]);

  const [activeTag, setActiveTag] = useState('all');
  const [tagSearchCache, setTagSearchCache] = useState({});

  // Reset to 'all' if tags change
  useEffect(() => {
    setActiveTag('all');
  }, [data.filterTags]);

  // When a tag is clicked, dispatch a new search with that tag as a Subject filter
  const handleTagClick = (tag) => {
    setActiveTag(tag);
    if (tag === 'all') return; // No need to refetch, use base results

    // Check if we already have cached results for this tag
    if (tagSearchCache[tag]) return;

    // Build a search request that adds the tag as a Subject filter
    if (data.useListing && data.query) {
      const baseOptions = {
        metadata_fields: [
          'title',
          'description',
          'image_field',
          'image_scales',
          'preview_image_scales',
          'preview_image_field',
          'lead_image',
          'effective',
          'start',
          'remoteUrl',
          'Subject',
        ],
      };

      let searchOptions;
      let searchTerm = '';

      if (typeof data.query === 'object' && data.query !== null) {
        if (data.query.query && Array.isArray(data.query.query)) {
          const transformedQuery = {};
          let pathPending = false;

          data.query.query.forEach((criterion) => {
            if (!criterion.i) return;
            if (
              criterion.i === 'path' &&
              criterion.o ===
                'plone.app.querystring.operation.string.absolutePath' &&
              typeof criterion.v === 'string'
            ) {
              const [uid, depthStr] = criterion.v.split('::');
              const resolvedPath = resolvedPaths[uid];
              if (!resolvedPath) {
                pathPending = true;
                return;
              }
              transformedQuery.path = resolvedPath;
              if (depthStr !== undefined) {
                transformedQuery['path.depth'] = parseInt(depthStr, 10);
              }
            } else if (criterion.i !== 'Subject') {
              // Copy non-Subject criteria as-is
              const keyMap = {
                portal_type: 'portal_type',
                review_state: 'review_state',
              };
              const key = keyMap[criterion.i] || criterion.i;
              transformedQuery[key] = criterion.v;
            }
            // Skip Subject criteria; we'll add the clicked tag instead
          });

          if (pathPending) return;
          if (data.query.sort_on) transformedQuery.sort_on = data.query.sort_on;
          if (data.query.sort_order)
            transformedQuery.sort_order = data.query.sort_order;
          if (data.query.limit)
            transformedQuery.b_size = parseInt(data.query.limit, 10);

          // Add the clicked tag as a Subject filter
          transformedQuery.Subject = tag;
          searchOptions = { ...transformedQuery, ...baseOptions };
        } else {
          searchOptions = { ...data.query, ...baseOptions };
          if (data.query.limit)
            searchOptions.b_size = parseInt(data.query.limit, 10);
          searchOptions.Subject = tag;
        }
      } else if (typeof data.query === 'string') {
        searchTerm = data.query;
        searchOptions = { ...baseOptions, Subject: tag };
      } else {
        searchOptions = { ...baseOptions, query: data.query, Subject: tag };
      }

      if (data.excludeFromNav) {
        searchOptions.exclude_from_nav = false;
      }

      // Dispatch search with tag-specific subrequest ID
      const tagSearchId = `${id}-tag-${tag}`;
      dispatch(searchContent(searchTerm, searchOptions, tagSearchId));

      // Mark this tag as being requested
      setTagSearchCache((prev) => ({ ...prev, [tag]: true }));
    }
  };

  const allSlides = getSlides();

  // When a tag is active, try to use tag-specific search results instead of client-side filtering
  const tagSearchId = activeTag !== 'all' ? `${id}-tag-${activeTag}` : null;
  const tagSearchResults = useSelector((state) =>
    tagSearchId ? state.search?.subrequests?.[tagSearchId] : null,
  );

  // If we have tag-specific results, use those; otherwise fall back to filtered allSlides
  const displayedSlides = React.useMemo(() => {
    // Helper to convert items to slides inline (avoid dependency issues)
    const itemToSlide = (item) => ({
      heading: item.title || '',
      content: item.description || '',
      image: null,
      link: item,
      buttonText: data.listingButtonText || 'Read more',
      effectiveDate: item.effective || null,
      startDate: item.start || null,
      subjects: item.Subject || [],
      '@id': item['@id'] || null,
      url: item['@id'] ? flattenToAppURL(item['@id']) : null,
      image_field: item.image_field || null,
      image_scales: item.image_scales || null,
      preview_image_scales: item.preview_image_scales || null,
      preview_image_field: item.preview_image_field || null,
      lead_image: item.lead_image || null,
      isFromListing: true,
    });

    if (
      activeTag !== 'all' &&
      tagSearchResults?.items &&
      Array.isArray(tagSearchResults.items)
    ) {
      // Convert tag search results to slides
      return tagSearchResults.items.map(itemToSlide);
    }
    // Fall back to client-side filtering for 'all' or if tag results haven't loaded yet
    if (
      !Array.isArray(parsedFilterTags) ||
      parsedFilterTags.length === 0 ||
      activeTag === 'all'
    ) {
      return Array.isArray(allSlides) ? allSlides : [];
    }
    return Array.isArray(allSlides)
      ? allSlides.filter((slide) => {
          const subjects = slide.subjects || slide.link?.Subject || [];
          return Array.isArray(subjects)
            ? subjects.includes(activeTag)
            : subjects === activeTag;
        })
      : [];
  }, [
    activeTag,
    tagSearchResults,
    allSlides,
    parsedFilterTags,
    data.listingButtonText,
  ]);

  // Determine which tags actually have matching slides
  const tagsWithSlides = React.useMemo(() => {
    if (!Array.isArray(parsedFilterTags) || !Array.isArray(allSlides))
      return [];
    return parsedFilterTags.filter((tag) =>
      allSlides.some((slide) => {
        const subjects = slide.subjects || slide.link?.Subject || [];
        return Array.isArray(subjects)
          ? subjects.includes(tag)
          : subjects === tag;
      }),
    );
  }, [parsedFilterTags, allSlides]);

  const slides = displayedSlides;

  const slidesToShow = parseInt(data.slidesToShow, 10) || 1;
  const minSlidesOnMobile = parseInt(data.minSlidesOnMobile, 10) || 1;

  const calculateSlidesToShow = React.useCallback(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
    if (w <= 540) return Math.max(1, minSlidesOnMobile);
    if (w <= 768) return Math.min(slidesToShow, Math.max(1, minSlidesOnMobile));
    return slidesToShow;
  }, [slidesToShow, minSlidesOnMobile]);

  const [effectiveSlidesToShow, setEffectiveSlidesToShow] = useState(
    calculateSlidesToShow,
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  );

  useEffect(() => {
    const onResize = () => {
      setEffectiveSlidesToShow(calculateSlidesToShow());
      setIsMobile(window.innerWidth <= 768);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [calculateSlidesToShow]);

  // Peek disabled on mobile and when fewer than 5 slides are visible
  const peekActive = data.centeredPeek && !isMobile && slides.length >= 5;

  useEffect(() => {
    if (data.useListing && data.query) {
      const baseOptions = {
        metadata_fields: [
          'title',
          'description',
          'image_field',
          'image_scales',
          'preview_image_scales',
          'preview_image_field',
          'lead_image',
          'effective',
          'start',
          'remoteUrl',
          'Subject',
        ],
      };

      let searchOptions;
      let searchTerm = '';

      if (typeof data.query === 'object' && data.query !== null) {
        if (data.query.query && Array.isArray(data.query.query)) {
          // Transform the Volto query widget's criteria array into flat searchContent params.
          // We do this generically so every criterion passes through regardless of index name.
          const transformedQuery = {};
          let pathPending = false;
          data.query.query.forEach((criterion) => {
            if (!criterion.i) return;
            if (
              criterion.i === 'path' &&
              criterion.o ===
                'plone.app.querystring.operation.string.absolutePath' &&
              typeof criterion.v === 'string'
            ) {
              const [uid, depthStr] = criterion.v.split('::');
              const resolvedPath = resolvedPaths[uid];
              if (!resolvedPath) {
                pathPending = true;
                return;
              }
              transformedQuery.path = resolvedPath;
              if (depthStr !== undefined) {
                transformedQuery['path.depth'] = parseInt(depthStr, 10);
              }
            } else {
              const keyMap = {
                portal_type: 'portal_type',
                Subject: 'Subject',
                review_state: 'review_state',
              };
              const key = keyMap[criterion.i] || criterion.i;
              transformedQuery[key] = criterion.v;
            }
          });
          if (pathPending) return; // don't dispatch until path is resolved
          if (data.query.sort_on) transformedQuery.sort_on = data.query.sort_on;
          if (data.query.sort_order)
            transformedQuery.sort_order = data.query.sort_order;
          if (data.query.limit)
            transformedQuery.b_size = parseInt(data.query.limit, 10);
          searchOptions = { ...transformedQuery, ...baseOptions };
        } else {
          // Query is already a flat object (e.g. set programmatically)
          searchOptions = { ...data.query, ...baseOptions };
          if (data.query.limit)
            searchOptions.b_size = parseInt(data.query.limit, 10);
        }
      } else if (typeof data.query === 'string') {
        searchTerm = data.query;
        searchOptions = baseOptions;
      } else {
        searchOptions = { ...baseOptions, query: data.query };
      }

      // Exclude items marked as "Exclude from navigation" if the option is on
      if (data.excludeFromNav) {
        searchOptions.exclude_from_nav = false;
      }

      dispatch(searchContent(searchTerm, searchOptions, id));
    }
  }, [data.useListing, data.query, resolvedPaths, dispatch, id]);

  const carouselType = data.carouselType || 'default';

  const configOverrides = {
    default: { align: 'start', loop: data.loop ?? true },
    centered: { align: 'center', loop: true },
    peek: { align: 'start', loop: true },
    cards: { align: 'start', loop: false, containScroll: 'trimSnaps' },
  };

  const presetConfig = getPresetConfig(data.preset);
  const baseConfig = configOverrides[carouselType] || configOverrides.default;
  const config = { ...baseConfig, ...presetConfig };

  const [selectedIndex, setSelectedIndex] = useState(0);

  const plugins = data.autoplay
    ? [
        Autoplay({
          delay: parseInt(data.autoplayDelay, 10) || 4000,
          stopOnInteraction: false,
        }),
      ]
    : [];
  const [viewportRef, embla] = useEmblaCarousel(
    {
      ...config,
      loop: data.loop ?? true,
      align: peekActive ? 'center' : 'start',
      slidesToScroll: peekActive ? 1 : effectiveSlidesToShow,
      ...(peekActive ? { containScroll: false } : {}),
    },
    plugins,
  );

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelectedIndex(embla.selectedScrollSnap());
    onSelect(); // set initial selected index
    embla.on('select', onSelect);
    embla.on('reInit', onSelect);
    return () => {
      try {
        embla.off('select', onSelect);
      } catch (e) {}
      try {
        embla.off('reInit', onSelect);
      } catch (e) {}
    };
  }, [embla]);

  // Scroll back to start whenever active tag changes
  useEffect(() => {
    if (!embla) return;
    embla.reInit();
    embla.scrollTo(0, true);
    setSelectedIndex(0);
  }, [activeTag, embla]);

  const getHref = (link) => {
    const item = Array.isArray(link) ? link[0] : link;
    if (!item || typeof item !== 'object') return '#';
    if (item['@type'] === 'Link' && item.remoteUrl) {
      const remote = item.remoteUrl;
      // External URL — use as-is
      if (remote.startsWith('http') || remote.startsWith('//')) return remote;
      // Internal resolveuid or relative — navigate to the Link item's own @id and let
      // Plone's Link content type redirect to the target. This avoids resolveuid in the URL.
      return item['@id'] ? flattenToAppURL(item['@id']) : '#';
    }
    const url = item['@id'] ? flattenToAppURL(item['@id']) : '';
    return url || '#';
  };

  const opensInNewTab = (link) => {
    const item = Array.isArray(link) ? link[0] : link;
    if (!item || typeof item !== 'object') return false;
    return item['@type'] === 'File' || item['@type'] === 'Link';
  };

  const _displayMode = data.displayMode || data.mode || 'full';
  const isLogoMarquee = _displayMode === 'logo-marquee';
  const isLogoStrip = _displayMode === 'logo-strip';
  const variantClass = `type-${_displayMode}`;
  const isImageOnly = _displayMode === 'image-only';
  const isImageTopMode = _displayMode === 'image-top';
  const isPartialOverlay = _displayMode === 'partial-overlay';
  const isOverlayMode =
    !isImageOnly && !isImageTopMode && !isLogoMarquee && !isLogoStrip;

  const renderLink = (slide) => {
    const href = getHref(slide.link);
    const label = slide.buttonText?.trim();
    if (!href || !label || data.hideButtons || _displayMode === 'image-only')
      return null;
    const isExternal =
      href.startsWith('http') && !href.includes(window.location.hostname);
    const isFile = opensInNewTab(slide.link);
    const opensNewTab = isExternal || isFile;
    const { className, style } = getButtonClasses(
      data.slideButtonStyle,
      'embla__slide-btn',
    );
    return (
      <a
        href={href}
        className={className}
        style={style}
        {...(opensNewTab
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
      >
        {label}
      </a>
    );
  };

  const handleSlideClick = (slide) => {
    if (!data.clickableSlides) return;
    const href = getHref(slide.link);
    if (!href || href === '#') return;
    const isExternal =
      href.startsWith('http') && !href.includes(window.location.hostname);
    const isFile = opensInNewTab(slide.link);
    if (isExternal || isFile) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = href;
    }
  };

  const imageFor = (slide) => {
    try {
      if (slide.isFromListing) {
        return getImageUrl(slide) || getListingImageUrl(slide.link);
      }
      const image = Array.isArray(slide.image) ? slide.image[0] : slide.image;
      if (!image?.['@id']) return image?.download || '';
      // The picked image comes back as a full catalog brain (image_field +
      // image_scales), same shape as listing items — reuse the same resolver
      // so we get a proper scale instead of the raw field traversal.
      const resolved = getImageUrl(image);
      if (resolved) return resolved;
      const imgUrl = flattenToAppURL(image['@id']);
      // Objects whose @id ends with an image extension can't use @@images traversal
      if (hasImageExtension(imgUrl)) return imgUrl;
      return `${imgUrl}/@@images/image`;
    } catch (e) {
      return '';
    }
  };

  const heightClass = data.equalHeight ? 'equal-height' : '';
  const modeClass = data.mode ? `mode-${data.mode}` : '';
  const colorClass = colorToClass(data.backgroundColor);
  const toneClass = data.textTone === 'light' ? 'tone-light' : 'tone-dark';
  // Image-top card tone follows the card's own background colour, not the
  // wrapper's textTone — an editor can set these independently.
  const cardToneClass = isColorDark(data.imageTopCardColor)
    ? 'card-tone-dark'
    : 'card-tone-light';
  const blockId = data.title
    ? data.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
    : undefined;

  const showArrows =
    data.hideArrows !== true &&
    slides.length > 1 &&
    !isLogoMarquee &&
    !isLogoStrip;
  const showDots =
    data.hideDots !== true &&
    slides.length > 0 &&
    !isLogoMarquee &&
    !isLogoStrip;

  const pageCount = peekActive
    ? slides.length
    : Math.ceil(slides.length / effectiveSlidesToShow);

  return (
    <div
      id={blockId}
      className={`embla block ${heightClass} ${data.isFullWidth ? 'full-width' : ''} ${peekActive ? 'centered-peek' : ''} align-${data.alignment || 'left'} ${modeClass} ${variantClass} ${toneClass} ${colorClass} ${data.outerClassName || ''}`}
      style={{ backgroundColor: data.backgroundColor || 'transparent' }}
    >
      <div className="embla__inner">
        {(isLogoMarquee || isLogoStrip) &&
        (data.title || data.description) ? (
          <div className="embla__header">
            {data.title ? <h2 className="embla__title">{data.title}</h2> : null}
            {data.description ? (
              <p className="embla__description">{data.description}</p>
            ) : null}
          </div>
        ) : null}
        {/* Static logo strip */}
        {isLogoStrip ? (
          <div
            className="logo-strip"
            style={{
              '--logo-max-height': `${parseInt(data.logoMaxHeight, 10) || 60}px`,
              '--logo-gap': `${parseInt(data.logoGap, 10) || 32}px`,
              '--logo-pad': `${parseInt(data.logoPadding, 10) || 8}px`,
            }}
          >
            {slides.map((slide, i) => {
              const src = imageFor(slide);
              if (!src) return null;
              const href = getHref(slide.link);
              const img = (
                <img
                  className="logo-strip__img"
                  src={src}
                  alt={slide.heading || 'Logo'}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              );
              return (
                <div className="logo-strip__item" key={i}>
                  {href && href !== '#' ? (
                    <a
                      href={href}
                      className="logo-strip__link"
                      {...(href.startsWith('http') || opensInNewTab(slide.link)
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                    >
                      {img}
                    </a>
                  ) : (
                    img
                  )}
                </div>
              );
            })}
          </div>
        ) : isLogoMarquee ? (
          /* Logo marquee */
          <>
            <div
              className={`logo-marquee ${data.pauseOnHover ? 'pause-on-hover' : ''}`}
              style={{
                '--marquee-duration': `${parseInt(data.marqueeSpeed, 10) || 20}s`,
                '--logo-height': `${parseInt(data.logoHeight, 10) || 48}px`,
                '--logo-max-width':
                  parseInt(data.logoMaxWidth, 10) > 0
                    ? `${parseInt(data.logoMaxWidth, 10)}px`
                    : 'none',
                '--logo-gap': `${parseInt(data.logoGap, 10) || 32}px`,
                '--logo-pad': `${parseInt(data.logoPadding, 10) || 8}px`,
              }}
            >
              <div className="logo-marquee__track">
                {[...slides, ...slides].map((slide, i) => {
                  const src = imageFor(slide);
                  if (!src) return null;
                  const alt = slide.heading || 'Logo';
                  const href = getHref(slide.link);
                  const clickable =
                    data.clickableSlides && href && href !== '#';
                  const img = (
                    <img
                      className="logo-marquee__img"
                      src={src}
                      alt={alt}
                      style={{
                        height: 'var(--logo-height, 48px)',
                        width: 'auto',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  );
                  return (
                    <div className="logo-marquee__item" key={`r-${i}`}>
                      {clickable ? (
                        <a
                          href={href}
                          className="logo-marquee__link"
                          target="_blank"
                        >
                          {img}
                        </a>
                      ) : (
                        img
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Header row — title/description left, arrows+button right when arrowPosition=top */}
            {(data.title ||
              data.description ||
              (showArrows && data.arrowPosition === 'top')) && (
              <div
                className={`embla__header ${data.arrowPosition === 'top' ? 'embla__header--has-controls' : ''}`}
              >
                <div className="embla__header-top">
                  {data.title ? (
                    <h2 className="embla__title">{data.title}</h2>
                  ) : null}
                  {showArrows &&
                    data.arrowPosition === 'top' &&
                    slides.length > 1 &&
                    (() => {
                      const arrowStyleParsed = getButtonClasses(
                        data.arrowStyle,
                        'embla__arrow',
                      );
                      const linkStyleParsed = getButtonClasses(
                        data.headerLinkStyle,
                        'embla__header-link',
                      );
                      return (
                        <div className="embla__header-controls embla__header-controls--desktop">
                          <button
                            className={`embla__prev ${arrowStyleParsed.className}`}
                            style={arrowStyleParsed.style}
                            onClick={() => embla && embla.scrollPrev()}
                          >
                            <i className="bi bi-arrow-left-short"></i>
                          </button>
                          {data.headerLinkText && (
                            <a
                              href={getHref(data.headerLinkUrl)}
                              className={linkStyleParsed.className}
                              style={linkStyleParsed.style}
                              {...(getHref(data.headerLinkUrl).startsWith(
                                'http',
                              )
                                ? {
                                    target: '_blank',
                                    rel: 'noopener noreferrer',
                                  }
                                : {})}
                            >
                              {data.headerLinkText}
                            </a>
                          )}
                          <button
                            className={`embla__next ${arrowStyleParsed.className}`}
                            style={arrowStyleParsed.style}
                            onClick={() => embla && embla.scrollNext()}
                          >
                            <i className="bi bi-arrow-right-short"></i>
                          </button>
                        </div>
                      );
                    })()}
                </div>
                {data.description ? (
                  <p className="embla__description">{data.description}</p>
                ) : null}
                {showArrows &&
                  data.arrowPosition === 'top' &&
                  slides.length > 1 &&
                  (() => {
                    const arrowStyleParsed = getButtonClasses(
                      data.arrowStyle,
                      'embla__arrow',
                    );
                    const linkStyleParsed = getButtonClasses(
                      data.headerLinkStyle,
                      'embla__header-link',
                    );
                    return (
                      <div className="embla__header-controls embla__header-controls--mobile">
                        <button
                          className={`embla__prev ${arrowStyleParsed.className}`}
                          style={arrowStyleParsed.style}
                          onClick={() => embla && embla.scrollPrev()}
                        >
                          <i className="bi bi-arrow-left-short"></i>
                        </button>
                        {data.headerLinkText && (
                          <a
                            href={getHref(data.headerLinkUrl)}
                            className={linkStyleParsed.className}
                            style={linkStyleParsed.style}
                            {...(getHref(data.headerLinkUrl).startsWith('http')
                              ? { target: '_blank', rel: 'noopener noreferrer' }
                              : {})}
                          >
                            {data.headerLinkText}
                          </a>
                        )}
                        <button
                          className={`embla__next ${arrowStyleParsed.className}`}
                          style={arrowStyleParsed.style}
                          onClick={() => embla && embla.scrollNext()}
                        >
                          <i className="bi bi-arrow-right-short"></i>
                        </button>
                      </div>
                    );
                  })()}
              </div>
            )}

            {/* Title/description standalone when no top controls */}
            {data.arrowPosition !== 'top' && !data.title && !data.description
              ? null
              : null}

            {/* Tag filter tabs */}
            {tagsWithSlides.length > 0 && (
              <div
                className="embla__filter-tabs"
                role="tablist"
                aria-label="Filter content"
              >
                <button
                  role="tab"
                  aria-selected={activeTag === 'all'}
                  className={`embla__filter-tab ${activeTag === 'all' ? 'is-active' : ''}`}
                  onClick={() => handleTagClick('all')}
                >
                  All
                </button>
                {tagsWithSlides.map((tag) => (
                  <button
                    key={tag}
                    role="tab"
                    aria-selected={activeTag === tag}
                    className={`embla__filter-tab ${activeTag === tag ? 'is-active' : ''}`}
                    onClick={() => handleTagClick(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
            <div className="embla__viewport" ref={viewportRef}>
              <div className="embla__container">
                {slides.map((slide, index) => {
                  const imageUrl = imageFor(slide);
                  const href = getHref(slide.link);
                  const isClickable =
                    data.clickableSlides && href && href !== '#';

                  return (
                    <div
                      className="embla__slide"
                      key={index}
                      style={{ flex: `0 0 ${100 / effectiveSlidesToShow}%` }}
                    >
                      <div className="slide__content">
                        <div
                          className={`carousel-slide-inner ${isClickable ? 'clickable' : ''} ${isImageOnly ? 'image-only' : ''} ${isImageTopMode ? 'image-top' : ''} ${isPartialOverlay ? 'partial-overlay' : ''} ${isOverlayMode && data.blurImage ? 'blur-image' : ''} ${slide.isFromListing ? 'listing-slide' : 'manual-slide'} ${!isImageOnly && !isImageTopMode && !imageUrl ? 'no-image' : ''}`}
                          style={{
                            backgroundImage:
                              !isImageOnly && !isImageTopMode && imageUrl
                                ? `url(${imageUrl})`
                                : 'none',
                            cursor: isClickable ? 'pointer' : 'default',
                          }}
                          onClick={() => handleSlideClick(slide)}
                        >
                          {isImageOnly ? (
                            imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={slide.heading || 'Slide image'}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="image-placeholder">No image</div>
                            )
                          ) : isImageTopMode ? (
                            <div
                              className={`card card-image-top ${cardToneClass}`}
                              style={
                                data.imageTopCardColor
                                  ? { backgroundColor: data.imageTopCardColor }
                                  : undefined
                              }
                            >
                              {imageUrl ? (
                                <div className="card-media">
                                  <img
                                    src={imageUrl}
                                    alt={slide.heading || 'Slide image'}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              ) : null}
                              <div className="card-content">
                                {renderDate(slide)}
                                <h3>{slide.heading}</h3>
                                {!data.hideDescription && (
                                  <p>{slide.content}</p>
                                )}
                                {renderLink(slide)}
                              </div>
                            </div>
                          ) : (
                            <div className="slide__body">
                              {renderDate(slide)}
                              <h3>{slide.heading}</h3>
                              {!data.hideDescription && <p>{slide.content}</p>}
                              {renderLink(slide)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {showArrows &&
              slides.length > 1 &&
              data.arrowPosition !== 'top' && (
                <div className="embla__controls" aria-hidden="false">
                  <div className="embla__controls__buttons" aria-hidden="false">
                    <button
                      className="embla__prev"
                      onClick={() => embla && embla.scrollPrev()}
                    >
                      <i className="bi bi-arrow-left-short"></i>
                    </button>
                    <button
                      className="embla__next"
                      onClick={() => embla && embla.scrollNext()}
                    >
                      <i className="bi bi-arrow-right-short"></i>
                    </button>
                  </div>
                </div>
              )}

            {showDots && pageCount > 1 && (
              <div className="embla__nav">
                {Array.from({ length: pageCount }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => embla && embla.scrollTo(i)}
                    className={`embla__dot ${i === selectedIndex ? 'is-selected' : ''}`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const WrappedEmblaCarousel = (props) => (
  <CarouselErrorBoundary>
    <EmblaCarousel {...props} />
  </CarouselErrorBoundary>
);

export default WrappedEmblaCarousel;
